import * as React from 'react';
import format from 'date-fns/format';
import { NumberFormatValues } from 'react-number-format';
import { List, Text } from '@mantine/core';

// Types
import { IQuestionConfig } from './types';
import { IContactRecord, ISalaryRange, UserTypes, IUserRole, IChildOrgRecord } from '../types';

// Helpers
import { RegExpClass } from '../helpers/RegExpClass';
import { formatPhoneNumber, numberAsDollar } from '../helpers/formatters';
import { getCurrentLocale } from '../helpers/locale';
import {
    PhoneNumberValidator,
    WageRangerValidation,
    CannotExceedTotalEmployeeCount,
    CannotExceedTotalContractorCount,
    HoursPerWeekValidation,
    MonthsOfYearCountValidation,
    NoNegativeInt,
    HouseholdCount,
    FinancialsAtLeastOneValue,
    FinancialsRequiredItems
} from './validators';

// Enums
import { PRONOUNS } from '../enums/PRONOUNS';

import i18n from '../i18n';

// Components
import { WageRangerSummary } from '../components/Form/components/WageRanger/components/WageRangerSummary';
import { FinancialsTableReadOnly } from '../components/FinancialsTableReadOnly';

// Default Values
import { WageRangerDefaultEmployeeValues, WageRangerDefaultContractorValues } from './defaultValues';

export const manifest: Record<string, IQuestionConfig> = {
    CON0000: {
        component: 'ContactsModule',
        key: 'contactsModule',
        isModule: true,
        componentProps: {
            labelKey: 'contactsModule.label'
        },
        gridSpan: 12,
        controlledDataSet: {
            key: 'contacts'
        },
        defaultValue: []
    },
    // Confirm owners module
    CON0000B: {
        component: 'ConfirmContactsModule',
        key: 'confirmContactsModule',
        isModule: true,
        componentProps: {
            labelKey: 'contactsModule.label'
        },
        subManifest: [
            {
                id: 'CON0020',
                componentProps: { alwaysTrue: true, required: true },
                validator: (val: any): string | null => {
                    const language = i18n.language.split('-')[0] as 'es' | 'en';

                    return !val || val === 0 ? (language === 'es' ? 'Obligatorio.' : 'Required.') : null;
                }
            },
            { id: 'CON0013' },
            { id: 'CON0011' },
            { id: 'CON0018' },
            { id: 'CON0017' }
        ],
        gridSpan: 12,
        controlledDataSet: {
            key: 'contacts',
            transform: (data, formValues) => {
                const contacts = (data ?? []) as IContactRecord[];
                const targetContactIds = [...new Set([...(formValues?.ownerCount ?? [])])];

                return contacts.filter(({ id }) => targetContactIds.includes(id));
            }
        },
        defaultValue: []
    },
    // Confirm executives module
    CON0000C: {
        component: 'ConfirmContactsModule',
        key: 'confirmContactsModule',
        isModule: true,
        componentProps: {
            labelKey: 'contactsModule.label'
        },
        subManifest: [{ id: 'CON0013' }, { id: 'CON0011' }, { id: 'CON0018' }, { id: 'CON0017' }],
        gridSpan: 12,
        controlledDataSet: {
            key: 'contacts',
            transform: (data, formValues) => {
                const contacts = (data ?? []) as IContactRecord[];
                const targetContactIds = [...new Set([...(formValues?.executiveCount ?? [])])];

                return contacts.filter(({ id }) => targetContactIds.includes(id));
            }
        },
        defaultValue: []
    },
    // Confirm primary contact details
    CON0000D: {
        component: 'PrimaryContactForm',
        key: 'confirmPrimaryContactDetails',
        isModule: true,
        componentProps: {
            labelKey: 'confirmPrimaryContactDetails.label'
        },
        gridSpan: 12,
        controlledDataSet: {
            key: 'organization',
            transform: (organization, formValues) => {
                return organization.primaryContact;
            }
        },
        defaultValue: []
    },
    CON0001: {
        component: 'TextInput',
        key: 'firstName',
        componentProps: {
            labelKey: 'firstName.label',
            required: true,
            maxLength: 100
        },
        gridSpan: 5
    },
    CON0002: {
        component: 'TextInput',
        key: 'middleInitial',
        componentProps: {
            labelKey: 'middleInitial.label',
            maxLength: 1
        },
        gridSpan: 2
    },
    CON0003: {
        component: 'TextInput',
        key: 'lastName',
        componentProps: {
            labelKey: 'lastName.label',
            required: true,
            maxLength: 100
        },
        gridSpan: 5
    },

    CON0004: {
        component: 'TextInput',
        key: 'ownerName',
        componentProps: {
            labelKey: 'ownerName.label',
            required: true,
            maxLength: 100
        },
        gridSpan: 4
    },
    // CON0004: {
    //     component: 'TextInput',
    //     key: 'ownerName',
    //     componentProps: {
    //         labelKey: 'ownerName.label',
    //         required: true,
    //         maxLength: 100
    //     },
    //     gridSpan: 4
    // },
    CON0005: {
        component: 'TextInput',
        key: 'email',
        componentProps: {
            labelKey: 'email.label',
            type: 'email',
            required: (ancillaryData = {}, formValues = {}) => {
                return Boolean(ancillaryData?.isPrimaryContact);
            },
            maxLength: 100
        },
        gridSpan: 4,
        validator: (val: string): string | null =>
            val && Boolean(val.match(RegExpClass.EMAIL)) ? null : 'email.invalidMessages.format'
    },
    CON0006: {
        component: 'TextInput',
        key: 'secondaryEmail',
        componentProps: {
            labelKey: 'secondaryEmail.label',
            type: 'email',
            maxLength: 100
        },
        gridSpan: 4,
        validator: (val: string): string | null =>
            val && Boolean(val.match(RegExpClass.EMAIL)) ? null : 'secondaryEmail.invalidMessages.format'
    },
    CON0007: {
        component: 'PhoneNumberGroup',
        key: 'phone',
        componentProps: {
            labelKey: 'phone.label'
        },
        validator: PhoneNumberValidator,
        gridSpan: 6,
        displayValueAs: formatPhoneNumber
    },
    CON0008: {
        component: 'PhoneNumberGroup',
        key: 'homePhone',
        componentProps: {
            labelKey: 'homePhone.label'
        },
        validator: PhoneNumberValidator,
        gridSpan: 6,
        displayValueAs: formatPhoneNumber
    },
    CON0009: {
        component: 'PhoneNumberGroup',
        key: 'mobilePhone',
        componentProps: {
            labelKey: 'mobilePhone.label'
        },
        validator: PhoneNumberValidator,
        gridSpan: 6,
        displayValueAs: formatPhoneNumber
    },
    CON0010: {
        component: 'TextInput',
        key: 'orgName',
        componentProps: {
            labelKey: 'orgName.label',
            type: 'orgName',
            required: true,
            maxLength: 100
        },
        gridSpan: 4
    },

    CON0011: {
        component: 'Select',
        key: 'gender',
        componentProps: {
            labelKey: 'gender.label',
            required: true
        },
        gridSpan: 6,
        dataSetKey: 'GENDER_CHOICES'
    },
    CON0013: {
        component: 'MultiSelect',
        key: 'raceEthnicity',
        componentProps: {
            labelKey: 'raceEthnicity.label',
            descriptionKey: 'raceEthnicity.description',
            required: true,
            clearable: true,
            onChangeCb: ({ value, changeFn }: { value: string[]; changeFn?: (value: string[]) => void }) => {
                // If newly added is 'Prefer Not to Answer' then deselect all others
                if (value[value.length - 1] === 'dna') {
                    changeFn?.(['dna']);
                } else if (value[value.length - 1] === 'idk') {
                    changeFn?.(['idk']);
                } else if (['dna', 'idk'].includes(value[0]) && value.length > 1) {
                    value.shift();
                    changeFn?.(value);
                }
            }
        },
        validator: (val: string): string | null => {
            return val && val.length > 0 ? null : 'Required.';
        },
        gridSpan: 12,
        dataSetKey: 'RACE_ETHNICITY'
    },
    ORG0014: {
        component: 'Select',
        key: 'clientRelationshipManager',
        componentProps: {
            labelKey: 'clientRelationshipManager.label',
            required: true
        },
        dataSetKey: 'CLIENT_RELATIONSHIP_MANAGER',
        writePermission: [IUserRole.CIC_OWNER, IUserRole.CIC_ADMIN],
        gridSpan: 6
    },
    CON0015: {
        component: 'NumberInput',
        key: 'portfolContactId',
        hidden: [UserTypes.AUTHENTICATED],
        componentProps: {
            labelKey: 'portfolContactId.label',
            // required: true,
            hideControls: true
        },
        gridSpan: 3
    },

    CON0016: {
        component: 'YesNoRadio',
        key: 'okToContact',
        hidden: [UserTypes.AUTHENTICATED],
        componentProps: {
            labelKey: 'okToContact.label'
            // required: true
        },
        disabled: (ancillaryData = {}, formValues = {}) => {
            return Boolean(ancillaryData?.isPrimaryContact);
        },
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 3,
        defaultValue: true
    },
    CON0017: {
        component: 'Select',
        key: 'disabled',
        componentProps: {
            labelKey: 'disabled.label',
            required: true
        },
        dataSetKey: 'YES_OR_NO',
        gridSpan: 6
    },
    CON0018: {
        component: 'Select',
        key: 'veteran',
        componentProps: {
            labelKey: 'veteran.label',
            required: true
        },
        dataSetKey: 'YES_OR_NO',
        gridSpan: 6
    },
    LOA0001: {
        component: 'TextInput',
        key: 'loanNumber',
        // hidden: true,
        componentProps: {
            labelKey: 'loanNumber.label',
            descriptionKey: 'loanNumber.description',
            required: true
        },
        validator: (val: string): string | null => {
            return val && Boolean(val.toString().match(RegExpClass.LOAN_NUMBER))
                ? null
                : 'loanNumber.invalidMessages.format';
        },
        gridSpan: 12
    },
    LOA0002: {
        component: 'NumericFormatInput',
        key: 'loanAmount',
        // hidden: true,
        componentProps: {
            labelKey: 'loanAmount.label',
            required: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        gridSpan: 6,
        displayValueAs: numberAsDollar
    },
    LOA0003: {
        component: 'NumericFormatInput',
        key: 'loanBalance',
        // hidden: true,
        componentProps: {
            labelKey: 'loanBalance.label',
            required: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        gridSpan: 6,
        displayValueAs: numberAsDollar
    },
    LOA0004: {
        component: 'DatePickerInput',
        key: 'actualSettlementDate',
        // hidden: true,
        componentProps: {
            labelKey: 'actualSettlementDate.label',
            required: true
        },
        gridSpan: 6,
        displayValueAs: (val) => (val ? format(new Date(val), 'PP', { locale: getCurrentLocale() }) : '--')
    },
    LOA0005: {
        component: 'DatePickerInput',
        key: 'effectiveTerminationDate',
        // hidden: true,
        componentProps: {
            labelKey: 'effectiveTerminationDate.label',
            required: false
        },
        gridSpan: 6,
        displayValueAs: (val) => (val ? format(new Date(val), 'PP', { locale: getCurrentLocale() }) : '--')
    },
    LOA0006: {
        component: 'YesNoRadio',
        key: 'closed',
        // hidden: true,
        componentProps: {
            labelKey: 'closed.label',
            required: true
        },
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 6,
        defaultValue: false
    },
    LOA0007: {
        component: 'Select',
        key: 'loanOfficer',
        componentProps: {
            labelKey: 'loanOfficer.label',
            required: true
        },
        dataSetKey: 'CLIENT_RELATIONSHIP_MANAGER',
        gridSpan: 6
    },
    LOA0008: {
        component: 'YesNoRadio',
        key: 'inDefault',
        hidden: [UserTypes.AUTHENTICATED],
        componentProps: {
            labelKey: 'inDefault.label',
            required: true
        },
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 6,
        defaultValue: false
    },
    LOA0009: {
        component: 'Select',
        key: 'loanCorp',
        componentProps: {
            labelKey: 'loanCorp.label',
            type: 'text',
            required: true,
            maxLength: 100
        },
        dataSetKey: 'LOAN_CORP',
        gridSpan: 6
    },
    LOA0010: {
        component: 'Select',
        key: 'loanPartner',
        componentProps: {
            labelKey: 'loanPartner.label',
            type: 'text',
            required: (ancillaryData = {}, formValues = {}) => {
                return !!formValues?.loanPartnerShare && !formValues?.loanPartner;
            },
            maxLength: 100
        },
        dataSetKey: 'LOAN_PARTNER',
        gridSpan: 6
    },
    LOA0011: {
        component: 'NumericFormatInput',
        key: 'loanPartnerShare',
        componentProps: {
            labelKey: 'loanPartnerShare.label',
            placeholder: ' %',
            suffix: ' %',
            prefix: '',
            decimalScale: 0,
            isAllowed: (values: NumberFormatValues) => {
                const { floatValue } = values;

                return typeof floatValue === 'undefined' || (floatValue > 0 && floatValue < 100);
            },
            required: (ancillaryData = {}, formValues = {}) => {
                return !!formValues?.loanPartner && !formValues?.loanPartnerShare;
            }
        },
        displayValueAs: (val) => {
            return typeof val !== 'undefined' && val !== null
                ? Number(val / 100).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0 })
                : '--';
        },
        gridSpan: 6
    },
    LOA0012: {
        component: 'TextInput',
        key: 'fromChildOrg',
        componentProps: {
            labelKey: 'fromChildOrg.label',
            descriptionKey: 'fromChildOrg.description',
            type: 'text',
            maxLength: 100
        },
        gridSpan: 12
    },

    ORG0001: {
        component: 'TextInput',
        key: 'clientNumber',
        componentProps: {
            type: 'number',
            labelKey: 'clientNumber.label',
            descriptionKey: 'clientNumber.description',
            required: true,
            maxLength: 3
        },
        gridSpan: 3,
        // permission: Permissions.READ,
        writePermission: [IUserRole.CIC_OWNER, IUserRole.CIC_ADMIN],
        hidden: [UserTypes.AUTHENTICATED],
        defaultValue: '',
        validator: (val: string | number): string | null => {
            const stringVal = typeof val === 'number' ? val.toString() : val;

            return Boolean(stringVal?.match(RegExpClass.ALL_NUMBERS)) && stringVal.length >= 1 && stringVal.length < 4
                ? null
                : 'clientNumber.invalidMessages.format';
        }
    },
    ORG0002: {
        component: 'TextInput',
        key: 'businessName',
        componentProps: {
            labelKey: 'businessName.label',
            // descriptionKey: 'businessName.description',
            // tooltipKey: 'businessName.tooltip',
            type: 'text',
            required: true,
            maxLength: 100
        },
        gridSpan: 6,
        defaultValue: ''
    },
    ORG0003: {
        component: 'TextInput',
        key: 'dba',
        componentProps: {
            labelKey: 'dba.label',
            descriptionKey: 'dba.description',
            type: 'text',
            required: false,
            maxLength: 100
        },
        gridSpan: 6,
        defaultValue: ''
    },

    ORG0004: {
        component: 'YesNoRadio',
        key: 'nonProfit',
        componentProps: {
            labelKey: 'nonProfit.label',
            required: true
        },
        dependentOn: {
            orgForm: 'C'
        },
        hideIfIrrelevant: true,
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 3
    },

    ORG0005: {
        component: 'NumberInput',
        key: 'daysAfterFiscalYearEnd',
        hidden: [UserTypes.AUTHENTICATED],
        componentProps: {
            labelKey: 'daysAfterFiscalYearEnd.label',
            hideControls: true,
            required: true
        },
        gridSpan: 6,
        defaultValue: 90
    },
    ORG0006: {
        component: 'Select',
        key: 'businessType',
        componentProps: {
            labelKey: 'businessType.label',
            required: true
        },
        dataSetKey: 'TYPE_OF_BUSINESS',
        gridSpan: 5
    },

    ORG0007: {
        component: 'YearMonthPickerInput',
        key: 'yearEstablished',
        componentProps: {
            labelKey: 'yearEstablished.label',
            required: true,
            noMinDate: true,
            maxDate: new Date()
        },
        dataSetKey: 'TYPE_OF_BUSINESS',
        gridSpan: 4,
        displayValueAs: (val) => {
            if (typeof val !== 'undefined') {
                let dateValue;
                if (val instanceof Date && !isNaN(val.valueOf())) {
                    dateValue = val;
                } else {
                    const date = new Date();
                    const stringVal = typeof val === 'number' ? val.toString() : val;

                    if (stringVal.length) {
                        const [year, month] = stringVal.match(/.{1,4}/g) ?? [];

                        if (typeof year !== 'undefined') date.setFullYear(parseInt(year));
                        date.setMonth(parseInt(month ?? 1) - 1);
                        date.setDate(1);
                    }
                    dateValue = date;
                }
                return format(dateValue, 'MMMM yyyy', { locale: getCurrentLocale() });
            }
            return '--';
        }
    },

    ORG0008: {
        component: 'PatternNumberInput',
        key: 'ein',
        componentProps: {
            labelKey: 'ein.label',
            required: true,
            pattern: '##-#######',
            mask: ' '
        },
        validator: (val: string): string | null => {
            return val &&
                (val.toString().match(RegExpClass.EIN_STRIPPED) || val.toString().match(RegExpClass.EIN_FORMATTED))
                ? null
                : 'ein.invalidMessages.format';
        },
        gridSpan: 3
    },

    ORG0009: {
        component: 'Select',
        key: 'orgForm',
        componentProps: {
            labelKey: 'orgForm.label',
            required: true
        },
        dataSetKey: 'ORGANIZATION_LEGAL_STRUCTURE',
        gridSpan: 6
    },

    ORG0010: {
        component: 'YesNoRadio',
        key: 'okToContactOrg',
        hidden: [UserTypes.AUTHENTICATED],
        componentProps: {
            labelKey: 'okToContact.label',
            required: true
        },
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 3,
        defaultValue: false
    },

    ORG0011: {
        component: 'MonthPickerInput',
        key: 'monthFiscalYearEnds',
        componentProps: {
            labelKey: 'monthFiscalYearEnds.label',
            required: true
        },
        gridSpan: 6,
        displayValueAs: (val) => {
            if (typeof val !== 'undefined') {
                let monthValue;
                if (val instanceof Date && !isNaN(val.valueOf())) {
                    monthValue = val;
                } else {
                    const date = new Date();
                    const month = Number(val) - 1;

                    date.setMonth(month);
                    monthValue = date;
                }
                return format(monthValue, 'MMMM', { locale: getCurrentLocale() });
            }
            return '--';
        }
    },

    ORG0012: {
        component: 'Select',
        key: 'financialContact',
        componentProps: {
            labelKey: 'financialContact.label',
            descriptionKey: 'financialContact.tooltip',
            tooltipKey: 'financialContact.description',
            required: true,
            searchable: true,
            nothingFoundKey: 'financialContact.nothingFound'
        },
        gridSpan: 6,
        // ancillaryDataKey: 'contacts'
        controlledDataSet: {
            key: 'contacts',
            transform: (data) => {
                const contacts = (data ?? []) as IContactRecord[];
                return contacts.map(({ name, email, id }) => ({
                    label: name?.fullName ?? '',
                    value: id,
                    description: !email?.primary ? 'Only contacts with an email may be set as financial contact' : null,
                    disabled: !email?.primary
                }));
            }
        }
        // controlledDataSet: {
        //     key: 'contactsModule',
        //     transform: (contacts = []) => {
        //         return contacts.map(({ CON0001: firstName, CON0003: lastName, CON0005: email, id }) => ({
        //             label: `${firstName} ${lastName}`,
        //             value: id,
        //             description: !email ? 'Only contacts with an email may be set as financial contact' : null,
        //             disabled: !email
        //         }));
        //     }
        // }
    },

    ORG0013: {
        component: 'Select',
        key: 'primaryContact',
        componentProps: {
            labelKey: 'primaryContact.label',
            // descriptionKey: 'primaryContact.description',
            tooltipKey: 'primaryContact.description',
            required: true,
            searchable: true,
            nothingFoundKey: 'primaryContact.nothingFound'
        },
        gridSpan: 6,
        // ancillaryDataKey: 'contacts'
        controlledDataSet: {
            key: 'contacts',
            transform: (data) => {
                const contacts = (data ?? []) as IContactRecord[];
                if (Array.isArray(contacts)) {
                    return contacts.map(({ name, email, id }) => ({
                        label: name?.fullName ?? '',
                        value: id,
                        description: !email?.primary
                            ? 'Only contacts with an email may be set as primary contact'
                            : null,
                        disabled: !email?.primary
                    }));
                } else {
                    return [];
                }
            }
        },
        displayValueAs: (val, ancillaryData = {}) => {
            if (!val) return '--';
            if (Array.isArray(ancillaryData?.contacts)) {
                const primary = ancillaryData?.contacts.find(({ id }) => id === val);

                return primary ? primary?.name?.fullName : val;
            }
            return val;
        }
    },

    ORG0015: {
        component: 'TextInput',
        key: 'orgStatus',
        writePermission: [],
        componentProps: {
            labelKey: 'orgStatus.label',
            type: 'text',
            required: false,
            maxLength: 100
        },
        gridSpan: 6,
        defaultValue: ''
    },

    ORG0016: {
        component: 'ListField',
        key: 'childOrgs',
        writePermission: [],
        componentProps: {
            labelKey: 'childOrgs.label',
            type: 'text',
            required: false,
            maxLength: 100
        },
        hideFromSummaryIfEmpty: true,
        displayValueAs: (val = []) => {
            return (
                <List my="sm" listStyleType="none" spacing="md">
                    {(val as IChildOrgRecord[]).map(({ name, inheritedLoans = [] }, index) => (
                        <List.Item key={index}>
                            <Text>{name}</Text>
                            <Text size="xs" c="dimmed">
                                Inherited loans: {inheritedLoans.length > 0 ? inheritedLoans.join(', ') : '--'}
                            </Text>
                        </List.Item>
                    ))}
                </List>
            );
        },
        gridSpan: 6,
        defaultValue: ''
    },

    CON0019: {
        component: 'TextInput',
        key: 'jobTitle',
        componentProps: {
            labelKey: 'jobTitle.label',
            maxLength: 100
        },
        gridSpan: 6,
        defaultValue: ''
    },

    CON0020: {
        component: 'PercentageOwnershipInput',
        key: 'percentOwnership',
        componentProps: {
            labelKey: 'percentOwnership.label',
            suffix: ' %',
            prefix: '',
            decimalScale: 2,
            isAllowed: (values: NumberFormatValues) => {
                const { floatValue } = values;

                return typeof floatValue === 'undefined' || floatValue < 100;
            },
            selectProps: {
                labelKey: 'percentOwnership.checkbox.label'
            },
            required: (ancillaryData = {}, formValues = {}) => {
                if (typeof ancillaryData?.parentFormState?.nonProfit !== 'undefined')
                    return !ancillaryData?.parentFormState?.nonProfit;
                return !ancillaryData?.organization?.isNonProfit;
            }
        },
        gridSpan: 12
    },

    CON0022: {
        component: 'TextInput',
        key: 'suffix',
        componentProps: {
            labelKey: 'suffix.label',
            maxLength: 100,
            placeholderkey: 'suffix.placeholder'
        },
        gridSpan: 4
    },

    CON0023: {
        component: 'TextInput',
        key: 'honorific',
        componentProps: {
            labelKey: 'honorific.label',
            maxLength: 100,
            placeholderkey: 'honorific.placeholder'
        },
        gridSpan: 4
    },

    CON0024: {
        component: 'Select',
        key: 'pronouns',
        componentProps: {
            labelKey: 'pronouns.label',
            maxLength: 100,
            placeholderkey: 'pronouns.placeholder',
            tooltipKey: 'pronouns.tooltip',
            creatable: true,
            searchable: true,
            createValidation: (val: string): boolean =>
                (val && !Boolean(val.match(RegExpClass.SPECIAL_CHARS_OTHER_THAN_FORWARD_SLASH))) || false
        },
        gridSpan: 4,
        dataSetKey: 'PRONOUNS',
        displayValueAs: (val) => {
            if (!val) return '--';
            const language = i18n.language.split('-')[0] as 'es' | 'en';

            return PRONOUNS?.[val]?.[language] ?? val;
        }
    },

    // Other portfol fields
    ASP0001: {
        component: 'Textarea',
        key: 'businessDetail',
        // hidden: true,
        componentProps: {
            labelKey: 'businessDetail.label',
            required: true
        }
    },

    ASP0002: {
        component: 'YesNoRadio',
        key: 'stillRunning',
        componentProps: {
            labelKey: 'stillRunning.label',
            required: true
        },
        gridSpan: 4
    },

    ASP0003: {
        component: 'DatePickerInput',
        key: 'businessCloseDate',
        // hidden: true,
        componentProps: {
            labelKey: 'businessCloseDate.label',
            required: true,
            maxDate: new Date()
        },
        dependentOn: {
            stillRunning: false
        },
        displayValueAs: (val) => (val ? format(new Date(val), 'PP', { locale: getCurrentLocale() }) : '--'),
        gridSpan: 6
    },

    ASP0004: {
        component: 'Select',
        key: 'businessCloseReason',
        componentProps: {
            labelKey: 'businessCloseReason.label',
            required: true
        },
        dependentOn: {
            stillRunning: false
        },
        dataSetKey: 'BUSINESS_CLOSE_REASON',
        gridSpan: 6
    },

    ASP0005: {
        component: 'Textarea',
        key: 'businessCloseReasonDescription',
        componentProps: {
            labelKey: 'businessCloseReasonDescription.label',
            required: false
        },
        dependentOn: {
            stillRunning: false,
            businessCloseReason: 'other'
        },
        gridSpan: 12
    },

    ASP0006: {
        component: 'MultiSelect',
        key: 'ownerCount',
        componentProps: {
            labelKey: 'ownerCount.label',
            descriptionKey: 'ownerCount.description',
            required: true
        },
        hideIfIrrelevant: true,
        dependentOn: {
            nonProfit: (val: boolean | undefined) => {
                return val !== true;
            }
        },
        // component: 'NumberInput',
        // key: 'ownerCount',
        // componentProps: {
        //     labelKey: 'ownerCount.label',
        //     required: true,
        //     hideControls: true
        // },
        controlledDataSet: {
            key: 'contacts',
            transform: (data) => {
                const contacts = (data ?? []) as IContactRecord[];
                return contacts.map(({ name, email, id }) => ({
                    label: name?.fullName ?? '',
                    value: id
                    // description: !email?.primary ? 'Only contacts with an email may be set as primary contact' : null,
                    // disabled: !email?.primary
                }));
            }
        },
        displayValueAs: (val, ancillaryData = {}) => {
            if (!val) return '--';
            if (Array.isArray(val)) {
                return val
                    .map((contactId) => {
                        const contact = (ancillaryData?.contacts as IContactRecord[])?.find(
                            ({ id }) => id === contactId
                        );

                        return contact?.name?.fullName ?? val;
                    })
                    .join(', ');
            }
            return val;
        },
        gridSpan: 12
    },

    ASP0007: {
        component: 'MultiSelect',
        key: 'executiveCount',
        componentProps: {
            labelKey: 'executiveCount.label',
            required: true
        },
        hideIfIrrelevant: true,
        dependentOn: {
            nonProfit: (val: boolean | undefined) => {
                return val === true;
            }
            // ancillaryData: (data: Record<string, any>) => {
            //     return data?.organization?.isNonProfit;
            // }
        },
        controlledDataSet: {
            key: 'contacts',
            transform: (data) => {
                const contacts = (data ?? []) as IContactRecord[];
                return contacts.map(({ name, email, id }) => ({
                    label: name?.fullName ?? '',
                    value: id
                    // description: !email?.primary ? 'Only contacts with an email may be set as primary contact' : null,
                    // disabled: !email?.primary
                }));
            }
        },
        displayValueAs: (val, ancillaryData = {}) => {
            if (!val) return '--';
            if (Array.isArray(val)) {
                return val
                    .map((contactId) => {
                        const contact = (ancillaryData?.contacts as IContactRecord[])?.find(
                            ({ id }) => id === contactId
                        );

                        return contact?.name?.fullName ?? val;
                    })
                    .join(', ');
            }
            return val;
        },
        gridSpan: 12
    },

    ASP0008: {
        component: 'Select',
        key: 'goalForLoan',
        componentProps: {
            labelKey: 'goalForLoan.label',
            required: true
        },
        dataSetKey: 'ASSISTANCE_TYPE',
        gridSpan: 12
    },
    ASP0009: {
        component: 'Select',
        key: 'whichFinancialProblem',
        componentProps: {
            labelKey: 'whichFinancialProblem.label',
            required: true
        },
        hideIfIrrelevant: true,
        dependentOn: {
            goalForLoan: 'f_problem'
        },
        dataSetKey: 'IMMEDIATE_PROBLEM_TYPE',
        gridSpan: 12
    },
    ASP0010: {
        component: 'Textarea',
        key: 'describeFinancialProblem',
        componentProps: {
            labelKey: 'describeFinancialProblem.label',
            required: false
        },
        hideIfIrrelevant: true,
        dependentOn: {
            goalForLoan: (val: string) => {
                return ['nf_problem', 'other'].includes(val);
            }
        },
        gridSpan: 12
    },
    ASP0011: {
        component: 'YesNoRadio',
        key: 'previousLoanApplication',
        componentProps: {
            labelKey: 'previousLoanApplication.label',
            required: true
        },
        gridSpan: 12
    },
    ASP0012: {
        component: 'Select',
        key: 'wereExpectationsMet',
        componentProps: {
            labelKey: 'wereExpectationsMet.label',
            labelHasMarkup: true,
            required: true
        },
        dependentOn: {
            previousLoanApplication: true
        },
        dataSetKey: 'CIC_EXPECTATIONS',
        gridSpan: 12
    },
    ASP0013: {
        component: 'Select',
        key: 'experienceCompare',
        componentProps: {
            labelKey: 'experienceCompare.label',
            labelHasMarkup: true,
            descriptionKey: 'experienceCompare.description',
            required: true
        },
        dependentOn: {
            previousLoanApplication: true
        },
        dataSetKey: 'CIC_EXPERIENCE_COMPARED',
        gridSpan: 12
    },
    ASP0014: {
        component: 'Textarea',
        key: 'experienceCompareDescription',
        componentProps: {
            labelKey: 'experienceCompareDescription.label',
            required: false
        },
        dependentOn: {
            previousLoanApplication: true
        },
        gridSpan: 12
    },
    ASP0015: {
        component: 'MultiSelect',
        key: 'reasonForLoanApproval',
        componentProps: {
            labelKey: 'reasonForLoanApproval.label',
            descriptionKey: 'reasonForLoanApproval.description',
            required: true
        },
        dataSetKey: 'REASON_FOR_LOAN_APPROVAL',
        gridSpan: 12
    },

    ASP0016: {
        component: 'Select',
        key: 'extentOfGoalsAccomplished',
        componentProps: {
            labelKey: 'extentOfGoalsAccomplished.label',
            required: true
        },
        dataSetKey: 'GOAL_COMPLETION',
        gridSpan: 12
    },
    ASP0017: {
        component: 'YesNoRadio',
        key: 'accomplishedOtherGoals',
        componentProps: {
            labelKey: 'accomplishedOtherGoals.label',
            required: true
        },
        gridSpan: 12
    },
    ASP0018: {
        component: 'Select',
        key: 'otherGoalsAccomplished',
        componentProps: {
            labelKey: 'otherGoalsAccomplished.label',
            required: true
        },
        dataSetKey: 'ASSISTANCE_TYPE',
        gridSpan: 12
    },
    ASP0019: {
        component: 'Select',
        key: 'extentGoalsAccomplishedByLoan',
        componentProps: {
            labelKey: 'extentGoalsAccomplishedByLoan.label',
            required: true
        },
        dataSetKey: 'GOAL_COMPLETION',
        gridSpan: 12
    },
    ASP0020: {
        component: 'Select',
        key: 'revenueSatisfaction',
        componentProps: {
            labelKey: 'revenueSatisfaction.label',
            required: true
        },
        dataSetKey: 'LEVEL_OF_SATISFACTION',
        gridSpan: 12
    },
    ASP0021: {
        component: 'Select',
        key: 'expectationMet',
        componentProps: {
            labelKey: 'expectationMet.label',
            required: true
        },
        dataSetKey: 'CIC_EXPECTATIONS',
        gridSpan: 6
    },
    ASP0022: {
        component: 'Textarea',
        key: 'suggestionsForCIC',
        componentProps: {
            labelKey: 'suggestionsForCIC.label',
            required: true
        },
        gridSpan: 12
    },
    ASP0023: {
        component: 'Textarea',
        key: 'challengesDescription',
        componentProps: {
            labelKey: 'challengesDescription.label',
            required: false
        },
        gridSpan: 12
    },
    ASP0024: {
        component: 'Select',
        key: 'likelyOtherCapitalSource',
        componentProps: {
            labelKey: 'likelyOtherCapitalSource.label',
            required: true
        },
        dataSetKey: 'RE_APPLY_LENDER',
        gridSpan: 12
    },
    ASP0025: {
        component: 'NumericFormatInput',
        key: 'grossSalesIncome',
        componentProps: {
            labelKey: 'grossSalesIncome.label',
            descriptionKey: 'grossSalesIncome.description',
            required: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        gridSpan: 12
    },

    ASP0026: {
        component: 'Select',
        key: 'businessPerformance',
        componentProps: {
            labelKey: 'businessPerformance.label',
            required: true
        },
        dataSetKey: 'BUSINESS_PERFORMANCE',
        gridSpan: 12
    },
    ASP0027: {
        component: 'Select',
        key: 'businessPerformanceMeetExpectations',
        componentProps: {
            labelKey: 'businessPerformanceMeetExpectations.label',
            required: true
        },
        dataSetKey: 'BUSINESS_PERFORMANCE_EXPECTATIONS',
        gridSpan: 12
    },
    ASP0028: {
        component: 'Textarea',
        key: 'pleaseExplainPreviousAnswer',
        componentProps: {
            labelKey: 'pleaseExplainPreviousAnswer.label',
            required: true
        },
        gridSpan: 12
    },
    ASP0029: {
        component: 'NumberInput',
        key: 'employeeCount',
        componentProps: {
            labelKey: 'employeeCount.label',
            descriptionKey: 'employeeCount.description',
            placeholderkey: 'employeeCount.placeholder',
            required: true,
            hideControls: true,
            smallerInput: true
        },
        validator: NoNegativeInt,
        gridSpan: 12
    },
    ASP0030: {
        component: 'NumberInput',
        key: 'offeredHealthInsuranceCount',
        componentProps: {
            labelKey: 'offeredHealthInsuranceCount.label',
            placeholderkey: 'offeredHealthInsuranceCount.placeholder',
            required: true,
            smallerInput: true,
            hideControls: true
        },
        dependentOn: {
            employeeCount: (val: number) => {
                return typeof val !== 'undefined' && val > 0;
            }
        },
        validator: CannotExceedTotalEmployeeCount,
        gridSpan: 12
    },
    ASP0031: {
        component: 'NumberInput',
        key: 'offeredPtoCount',
        componentProps: {
            labelKey: 'offeredPtoCount.label',
            placeholderkey: 'offeredPtoCount.placeholder',
            required: true,
            hideControls: true,
            smallerInput: true
        },
        dependentOn: {
            employeeCount: (val: number) => {
                return typeof val !== 'undefined' && val > 0;
            }
        },
        validator: CannotExceedTotalEmployeeCount,
        gridSpan: 12
    },
    ASP0032: {
        component: 'NumberInput',
        key: 'offeredRetirementPlanCount',
        componentProps: {
            labelKey: 'offeredRetirementPlanCount.label',
            placeholderkey: 'offeredRetirementPlanCount.placeholder',
            required: true,
            hideControls: true,
            smallerInput: true
        },
        dependentOn: {
            employeeCount: (val: number) => {
                return typeof val !== 'undefined' && val > 0;
            }
        },
        validator: CannotExceedTotalEmployeeCount,
        gridSpan: 12
    },
    ASP0033: {
        component: 'NumberInput',
        key: 'offeredRaiseCount',
        componentProps: {
            labelKey: 'offeredRaiseCount.label',
            placeholderkey: 'offeredRaiseCount.placeholder',
            required: true,
            hideControls: true,
            smallerInput: true
        },
        dependentOn: {
            employeeCount: (val: number) => {
                return typeof val !== 'undefined' && val > 0;
            }
        },
        validator: CannotExceedTotalEmployeeCount,
        gridSpan: 12
    },
    ASP0034: {
        component: 'WageRanger',
        key: 'classifyEmployeeWages',
        componentProps: {
            labelKey: 'classifyEmployeeWages.label',
            descriptionKey: 'classifyEmployeeWages.description',
            required: true
        },
        validator: WageRangerValidation.bind(null, 'employee'),
        gridSpan: 12,
        displayValueAs: (val = [], ancillaryData = {}) => {
            return <WageRangerSummary data={val as ISalaryRange[]} />;
        },
        defaultValue: WageRangerDefaultEmployeeValues
    },
    ASP0035: {
        component: 'NumberInput',
        key: 'fullTimeEmployeeCount',
        componentProps: {
            labelKey: 'fullTimeEmployeeCount.label',
            required: true,
            hideControls: true
        },
        gridSpan: 6
    },
    ASP0036: {
        component: 'NumberInput',
        key: 'partTimeEmployeeCount',
        componentProps: {
            labelKey: 'partTimeEmployeeCount.label',
            required: true,
            hideControls: true
        },
        gridSpan: 6
    },
    ASP0037: {
        component: 'YesNoRadio',
        key: 'wereContractorsHired',
        componentProps: {
            labelKey: 'wereContractorsHired.label',
            required: true
        },
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 12
    },
    ASP0038: {
        component: 'NumberOrNoAnswerRadioGroup',
        key: 'contractorsWithHourlyRateCount',
        componentProps: {
            labelKey: 'contractorsWithHourlyRateCount.label',
            required: true,
            type: 'number'
        },
        dependentOn: {
            wereContractorsHired: true
        },
        gridSpan: 12
    },
    // DEPRECATED
    // ASP0039: {
    //     component: 'TextInput',
    //     key: 'contractorsPaidPerJobCount',
    //     componentProps: {
    //         labelKey: 'contractorsPaidPerJobCount.label',
    //         required: true,
    //         placeholder: ''
    //     },
    //     gridSpan: 6,
    // },
    ASP0040: {
        component: 'WageRanger',
        key: 'classifyContractorsPay',
        componentProps: {
            labelKey: 'classifyContractorsPay.label',
            required: true,
            isContractors: true
        },
        validator: WageRangerValidation.bind(null, 'contractor'),
        gridSpan: 12,
        dependentOn: {
            wereContractorsHired: true,
            contractorsWithHourlyRateCount: (val: number) => {
                return val > 0;
            }
        },
        displayValueAs: (val = [], ancillaryData = {}) => {
            return <WageRangerSummary data={val as ISalaryRange[]} />;
        },
        defaultValue: WageRangerDefaultContractorValues
    },
    ASP0041: {
        component: 'NumberInput',
        key: 'contractorsFullPartTime',
        componentProps: {
            labelKey: 'contractorsFullPartTime.label',
            required: true,
            hideControls: true
        },
        validator: CannotExceedTotalContractorCount,
        gridSpan: 6
    },
    ASP0042: {
        component: 'NumericFormatInput',
        key: 'personalPay',
        componentProps: {
            labelKey: 'personalPay.label',
            required: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        gridSpan: 12
    },
    ASP0043: {
        component: 'NumberInput',
        key: 'personalWeeklyHours',
        componentProps: {
            labelKey: 'personalWeeklyHours.label',
            placeholderkey: 'personalWeeklyHours.placeholder',
            required: true,
            hideControls: true
        },
        validator: HoursPerWeekValidation,
        gridSpan: 12
    },
    ASP0044: {
        component: 'NumberInput',
        key: 'personalMonthsWorkedPerYear',
        componentProps: {
            labelKey: 'personalMonthsWorkedPerYear.label',
            placeholderkey: 'personalMonthsWorkedPerYear.placeholder',
            required: true,
            hideControls: true
        },
        validator: MonthsOfYearCountValidation,
        gridSpan: 12
    },
    ASP0045: {
        component: 'Select',
        key: 'personalPayMeetExpectations',
        componentProps: {
            labelKey: 'personalPayMeetExpectations.label',
            required: true
        },
        dataSetKey: 'BUSINESS_EXPECTATIONS',
        gridSpan: 12
    },
    ASP0046: {
        component: 'Select',
        key: 'reasonExpectationsNotMet',
        componentProps: {
            labelKey: 'reasonExpectationsNotMet.label',
            required: true
        },
        dependentOn: {
            personalPayMeetExpectations: (val: string) => {
                return ['some_met', 'not_met'].includes(val);
            }
        },
        hideIfIrrelevant: true,
        dataSetKey: 'BUSINESS_EXPECTATIONS_NOT_MET',
        gridSpan: 12
    },
    ASP0047: {
        component: 'Textarea',
        key: 'expectationNotMetDescription',
        componentProps: {
            labelKey: 'expectationNotMetDescription.label',
            required: false
        },
        hideIfIrrelevant: true,
        dependentOn: {
            personalPayMeetExpectations: (val: string) => {
                return ['some_met', 'not_met'].includes(val);
            },
            reasonExpectationsNotMet: 'other'
        },
        gridSpan: 12
    },
    ASP0048: {
        component: 'Select',
        key: 'primaryOrSupplementalSourceOfIncome',
        componentProps: {
            labelKey: 'primaryOrSupplementalSourceOfIncome.label',
            required: true
        },
        dataSetKey: 'BUSINESS_INCOME_SOURCE',
        gridSpan: 12
    },
    ASP0049: {
        component: 'NumberInput',
        key: 'sharedIncomeOrExpensesCount',
        componentProps: {
            labelKey: 'sharedIncomeOrExpensesCount.label',
            descriptionKey: 'sharedIncomeOrExpensesCount.description',
            required: true,
            hideControls: true,
            min: 0
        },
        validator: HouseholdCount,
        gridSpan: 12
    },
    ASP0050: {
        component: 'YesNoRadio',
        key: 'publicAssistanceReceived',
        componentProps: {
            labelKey: 'publicAssistanceReceived.label',
            descriptionKey: 'publicAssistanceReceived.description',
            required: true
        },
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 12
    },
    ASP0051: {
        component: 'NumericFormatInput',
        key: 'totalHouseholdIncome',
        componentProps: {
            labelKey: 'totalHouseholdIncome.label',
            required: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        gridSpan: 12
    },
    ASP0052: {
        component: 'Select',
        key: 'householdIncomeComparison',
        componentProps: {
            labelKey: 'householdIncomeComparison.label',
            required: true
        },
        dataSetKey: 'HOUSEHOLD_INCOME_COMPARISON',
        gridSpan: 12
    },
    ASP0053: {
        component: 'YesNoRadio',
        key: 'ableToSaveMoney',
        componentProps: {
            labelKey: 'ableToSaveMoney.label',
            required: true
        },
        // dataSetKey: 'YES_OR_NO_BINARY',
        gridSpan: 12
    },
    // Input table. Lines items but calc total
    // TEST0001: {
    //     component: 'InputValueTable',
    //     key: 'costOfGoods',
    //     componentProps: {
    //         labelKey: 'costOfGoods.label',
    //         required: true
    //     },
    //     validator: CostTableValidator,
    //     gridSpan: 6
    // },
    // Test
    TEST0002: {
        component: 'NumberInput',
        key: 'fullTimeEmployeeCount',
        componentProps: {
            labelKey: 'fullTimeEmployeeCount.label',
            required: true,
            hideControls: true
        },
        gridSpan: 6
    },
    TEST0003: {
        component: 'NumberInput',
        key: 'partTimeEmployeeCount',
        componentProps: {
            labelKey: 'partTimeEmployeeCount.label',
            required: true,
            hideControls: true
        },
        gridSpan: 6
    },
    // Financials
    FIN0000A: {
        component: 'FinancialsTable',
        key: 'profitLossIncomeTable',
        isModule: true,
        isValueTable: true,
        componentProps: {
            labelKey: 'profitLossIncomeTable.label'
        },
        subManifest: [
            { id: 'FIN0001' },
            { id: 'FIN0002' },
            { id: 'FIN0003' },
            { id: 'FIN0004', componentProps: { sumOf: ['FIN0001', '-FIN0002', '-FIN0003'] } },
            { id: 'FIN0005' },
            { id: 'FIN0006', componentProps: { isTotal: true, sumOf: ['FIN0001', '-FIN0002', '-FIN0003', 'FIN0005'] } },
            { id: 'FIN0025', componentProps: { hidden: true, sumOf: ['FIN0006', '-FIN0024'] } }
        ],
        // getSideEffectUpdates: (values) => {
        //     return {
        //         FIN0025: (values?.['FIN0006'] ?? 0) - (values?.['FIN0024'] ?? 0)
        //     };
        // },
        answerStaticKeys: ['FIN0001', 'FIN0002', 'FIN0003', 'FIN0004', 'FIN0005', 'FIN0006'],
        validator: (val, values, requiredValues) => {
            return FinancialsRequiredItems(values, requiredValues);
        },
        displayValueAs: (data = {}) => {
            return <FinancialsTableReadOnly data={data} componentKey="FIN0000A" />;
        },
        gridSpan: 12,
        defaultValue: 0
    },
    FIN0000B: {
        component: 'FinancialsTable',
        key: 'profitLossExpensesTable',
        isModule: true,
        isValueTable: true,
        componentProps: {
            labelKey: 'profitLossExpensesTable.label'
        },
        subManifest: [
            { id: 'FIN0007' },
            { id: 'FIN0008' },
            { id: 'FIN0009' },
            { id: 'FIN0010' },
            { id: 'FIN0011' },
            { id: 'FIN0012' },
            { id: 'FIN0013' },
            { id: 'FIN0014' },
            { id: 'FIN0015' },
            { id: 'FIN0016' },
            { id: 'FIN0017' },
            { id: 'FIN0018' },
            { id: 'FIN0019' },
            { id: 'FIN0020' },
            { id: 'FIN0021' },
            { id: 'FIN0022' },
            { id: 'FIN0023' },
            {
                id: 'FIN0024',
                componentProps: {
                    isTotal: true,
                    sumOf: [
                        'FIN0007',
                        'FIN0008',
                        'FIN0009',
                        'FIN0010',
                        'FIN0011',
                        'FIN0012',
                        'FIN0013',
                        'FIN0014',
                        'FIN0015',
                        'FIN0016',
                        'FIN0017',
                        'FIN0018',
                        'FIN0019',
                        'FIN0020',
                        'FIN0021',
                        'FIN0022',
                        'FIN0023'
                    ]
                }
            },
            { id: 'FIN0025', componentProps: { hidden: true, sumOf: ['FIN0006', '-FIN0024'] } }
        ],
        answerStaticKeys: [
            'FIN0007',
            'FIN0008',
            'FIN0009',
            'FIN0010',
            'FIN0011',
            'FIN0012',
            'FIN0013',
            'FIN0014',
            'FIN0015',
            'FIN0016',
            'FIN0017',
            'FIN0018',
            'FIN0019',
            'FIN0020',
            'FIN0021',
            'FIN0022',
            'FIN0023',
            'FIN0024'
        ],
        displayValueAs: (data = {}) => {
            return <FinancialsTableReadOnly data={data} componentKey="FIN0000B" />;
        },
        validator: (val, values) => {
            return FinancialsAtLeastOneValue(values);
        },
        gridSpan: 12,
        defaultValue: 0
    },
    FIN0000C: {
        component: 'FinancialsTable',
        key: 'profitLossNetProfitTable',
        isModule: true,
        isValueTable: true,
        noReviewEdit: true,
        componentProps: {
            labelKey: 'profitLossNetProfitTable.label'
        },
        subManifest: [{ id: 'FIN0025' }],
        answerStaticKeys: ['FIN0025'],
        displayValueAs: (data = {}) => {
            return <FinancialsTableReadOnly totalHighlight data={data} componentKey="FIN0000C" />;
        },
        gridSpan: 12,
        defaultValue: 0
    },
    FIN0000D: {
        component: 'FinancialsTable',
        key: 'balanceSheetTable',
        isModule: true,
        isValueTable: true,
        componentProps: {
            labelKey: 'balanceSheetTable.label'
        },
        subManifest: [
            { id: 'FIN0026' },
            { id: 'FIN0027' },
            { id: 'FIN0028', componentProps: { isTotal: true, sumOf: ['FIN0026', '-FIN0027'] } }
        ],
        answerStaticKeys: ['FIN0026', 'FIN0027', 'FIN0028'],
        validator: (val, values, requiredValues) => {
            return FinancialsRequiredItems(values, requiredValues);
        },
        displayValueAs: (data = {}) => {
            return <FinancialsTableReadOnly data={data} componentKey="FIN0000D" />;
        },
        gridSpan: 12,
        defaultValue: 0
    },
    FIN0000E: {
        component: 'DefaultSubForm',
        key: 'balanceSheetTable',
        isModule: true,
        isValueTable: true,
        componentProps: {
            labelKey: 'balanceSheetTable.label'
        },
        subManifest: [
            { id: 'FIN0026' },
            { id: 'FIN0027' },
            { id: 'FIN0028', componentProps: { hidden: true, sumOf: ['FIN0026', '-FIN0027'] } }
        ],
        answerStaticKeys: ['FIN0026', 'FIN0027', 'FIN0028'],
        validator: (val, values, requiredValues) => {
            return FinancialsRequiredItems(values, requiredValues);
        },
        displayValueAs: (data = {}) => {
            return <FinancialsTableReadOnly data={data} componentKey="FIN0000E" />;
        },
        gridSpan: 12,
        defaultValue: 0
    },
    FIN0000F: {
        component: 'FinancialsTable',
        key: 'balanceSheetTableEquityTable',
        isModule: true,
        isValueTable: true,
        noReviewEdit: true,
        componentProps: {
            labelKey: 'balanceSheetTableEquityTable.label'
        },
        subManifest: [{ id: 'FIN0028' }],
        answerStaticKeys: ['FIN0028'],
        displayValueAs: (data = {}) => {
            return <FinancialsTableReadOnly totalHighlight data={data} componentKey="FIN0000F" />;
        },
        gridSpan: 12,
        defaultValue: 0
    },
    FIN0001: {
        key: 'profitLossGrossReceipt',
        component: '',
        componentProps: {
            labelKey: 'profitLossGrossReceipt.label',
            required: true
        },
        displayValueAs: numberAsDollar
    },
    FIN0002: {
        key: 'profitLossReturns',
        component: '',
        componentProps: {
            labelKey: 'profitLossReturns.label',
            displayAsNegative: true
        },
        displayValueAs: numberAsDollar
    },
    FIN0003: {
        key: 'profitLossCostOfGoods',
        component: '',
        componentProps: {
            labelKey: 'profitLossCostOfGoods.label',
            displayAsNegative: true
        },
        displayValueAs: numberAsDollar
    },
    FIN0004: {
        key: 'profitLossGrossProfit',
        component: '',
        componentProps: {
            labelKey: 'profitLossGrossProfit.label',
            computed: true
        },
        displayValueAs: numberAsDollar
    },
    FIN0005: {
        key: 'profitLossOtherIncome',
        component: '',
        componentProps: {
            labelKey: 'profitLossOtherIncome.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0006: {
        key: 'profitLossGrossIncome',
        component: '',
        componentProps: {
            labelKey: 'profitLossGrossIncome.label',
            computed: true
        },
        displayValueAs: numberAsDollar
    },
    FIN0007: {
        key: 'profitLossExpensesAdvertising',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesAdvertising.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0008: {
        key: 'profitLossExpensesAuto',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesAuto.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0009: {
        key: 'profitLossExpensesContractLabor',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesContractLabor.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0010: {
        key: 'profitLossExpensesDepreciation',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesDepreciation.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0011: {
        key: 'profitLossExpensesInterest',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesInterest.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0012: {
        key: 'profitLossExpensesInsurance',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesInsurance.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0013: {
        key: 'profitLossExpensesLegal',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesLegal.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0014: {
        key: 'profitLossExpensesOffice',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesOffice.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0015: {
        key: 'profitLossExpensesRent',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesRent.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0016: {
        key: 'profitLossExpensesRepairs',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesRepairs.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0017: {
        key: 'profitLossExpensesSupplies',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesSupplies.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0018: {
        key: 'profitLossExpensesTax',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesTax.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0019: {
        key: 'profitLossExpensesTravel',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesTravel.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0020: {
        key: 'profitLossExpensesMeals',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesMeals.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0021: {
        key: 'profitLossExpensesUtilities',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesUtilities.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0022: {
        key: 'profitLossExpensesWages',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesWages.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0023: {
        key: 'profitLossExpensesOther',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesOther.label'
        },
        displayValueAs: numberAsDollar
    },
    FIN0024: {
        key: 'profitLossExpensesTotal',
        component: '',
        componentProps: {
            labelKey: 'profitLossExpensesTotal.label',
            computed: true
        },
        displayValueAs: numberAsDollar
    },
    FIN0025: {
        key: 'profitLossNetProfit',
        component: 'TextInput',
        componentProps: {
            labelKey: 'profitLossNetProfit.label',
            tooltipKey: 'profitLossNetProfit.tooltip',
            computed: true,
            showTooltipOnReview: true
        },
        alwaysCompute: true,
        displayValueAs: numberAsDollar
        // answerStaticKeys: ['FIN0006', 'FIN0024'],
        // displayValueAs: (data = {}) => {
        //     const { profitLossExpensesTotal = 0, profitLossGrossIncome = 0 } = data;
        //     return numberAsDollar(profitLossGrossIncome - profitLossExpensesTotal);
        // },
    },
    FIN0026: {
        key: 'balanceSheetAssets',
        component: 'NumericFormatInput',
        componentProps: {
            labelKey: 'balanceSheetAssets.label',
            descriptionKey: 'balanceSheetAssets.description',
            required: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        recalculateKeysOnChange: ['FIN0028'],
        displayValueAs: numberAsDollar,
        gridSpan: 12
    },
    FIN0027: {
        key: 'balanceSheetLiabilities',
        component: 'NumericFormatInput',
        componentProps: {
            labelKey: 'balanceSheetLiabilities.label',
            descriptionKey: 'balanceSheetLiabilities.description',
            required: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        recalculateKeysOnChange: ['FIN0028'],
        displayValueAs: numberAsDollar,
        gridSpan: 12
    },
    FIN0028: {
        key: 'balanceSheetEquity',
        component: 'NumericFormatInput',
        componentProps: {
            labelKey: 'balanceSheetEquity.label',
            computed: true,
            prefix: '$ ',
            placeholder: '$ ',
            thousandSeparator: ','
        },
        hidden: true,
        alwaysCompute: true,
        calculateValue: (values?: Record<string, any>) => {
            return (values?.['FIN0026'] ?? 0) - (values?.['FIN0027'] ?? 0);
        },
        displayValueAs: numberAsDollar,
        gridSpan: 12
    },
    FIN0029: {
        key: 'financialsEmployeeCount',
        component: 'NumberInput',
        componentProps: {
            labelKey: 'financialsEmployeeCount.label',
            descriptionKey: 'financialsEmployeeCount.description',
            required: true,
            hideControls: true
        },
        gridSpan: 12
    },
    FIN0030: {
        key: 'financialsEmployeeFullTime',
        component: 'NumberInput',
        componentProps: {
            labelKey: 'financialsEmployeeFullTime.label',
            descriptionKey: 'financialsEmployeeFullTime.description',
            required: true,
            hideControls: true
        },
        gridSpan: 12
    }

    // surveyQuestions001: {
    //     component: 'Select',
    //     key: 'businessLegalStructure',
    //     componentProps: {
    //         labelKey: 'businessLegalStructure.label',
    //         required: true
    //     },
    //     dataSetKey: 'ORGANIZATION_LEGAL_STRUCTURE',
    //     gridSpan: 6
    // },
    // surveyQuestions002: {
    //     component: 'DatePickerInput',
    //     key: 'operationStartDate',
    //     // hidden: true,
    //     componentProps: {
    //         labelKey: 'operationStartDate.label',
    //         required: true
    //     },
    //     gridSpan: 6
    // },

    // portfolFields018: {
    //     component: 'Select',
    //     key: 'activeLoan',
    //     // hidden: true,
    //     componentProps: {
    //         labelKey: 'activeLoan.label',
    //         required: true
    //     },
    //     dataSetKey: 'YES_OR_NO_BINARY',
    //     gridSpan: 3
    // },

    // portfolFields034: {
    //     component: 'TextInput',
    //     key: 'salutation',
    //     componentProps: {
    //         labelKey: 'salutation.label',
    //         required: true,
    //         maxLength: 100
    //     },
    //     gridSpan: 4,
    //     defaultValue: '' // Allow for defaultValue to be value from other field
    // },
};

export const questionsWithDependents = Object.values(manifest).reduce<Record<string, boolean>>((acc, curr) => {
    if (curr.dependentOn) {
        const keysWithDeps = Object.keys(curr.dependentOn).reduce(
            (acc2, curr2) => ({
                ...acc,
                [curr2]: true
            }),
            {}
        );

        return {
            ...acc,
            ...keysWithDeps
        };
    } else {
        return acc;
    }
}, {});
